import { Box, Heading, HStack, Text, VStack } from "components/ui";
import { Spinner } from "components/ui/spinner";
import { toaster } from "components/ui/toaster";
import { getHelpVideoUrl } from "config/helpVideos";
import { useOnboarding } from "contexts/OnboardingContext";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { FaCheck, FaCircle } from "react-icons/fa";
import { authService } from "services/authService";
import { companyService, OnboardingState } from "services/companyService";
import { guardrailsService } from "services/guardrailsService";
import serverGenerationHubService from "services/serverGenerationHubService";
import { ApiDocType, serverService } from "services/serverService";
import { toolService } from "services/toolService";
import { widgetService } from "services/widgetService";
import { getThemeColor } from "theme/colors";
import { Step0Overview } from "./steps/Step0Overview";
import { Step1ApiDocumentation } from "./steps/Step1ApiDocumentation";
import { Step2Authorization } from "./steps/Step2Authorization";
import { Step3GenerationProgress } from "./steps/Step3GenerationProgress";
import { Step4ErrorHandling } from "./steps/Step4ErrorHandling";
import { Step5ToolSelection } from "./steps/Step5ToolSelection";
import { Step6Success } from "./steps/Step6Success";
import { Step7MaskingRulesReview } from "./steps/Step7MaskingRulesReview";

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const OnboardingModal = ({ isOpen, onClose }: OnboardingModalProps) => {
  const { t } = useTranslation();
  const { setIsOnboardingActive } = useOnboarding();
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingState, setOnboardingState] = useState<OnboardingState>({
    currentStep: 0,
  });
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setIsOnboardingActive(true);
      validateAndResumeOnboarding();
      // Disable body scroll when modal is open
      document.body.style.overflow = "hidden";
    } else {
      setIsOnboardingActive(false);
      // Re-enable body scroll when modal is closed
      document.body.style.overflow = "unset";
    }

    // Cleanup: re-enable scroll when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, setIsOnboardingActive]);

  const validateAndResumeOnboarding = async () => {
    try {
      const company = companyService.getCurrentCompany();

      // If onboarding is already completed, close modal
      if (company?.onboardingCompleted) {
        onClose();
        setIsValidating(false);
        return;
      }

      if (!company?.onboardingState) {
        // Fresh start
        setCurrentStep(0);
        setOnboardingState({ currentStep: 0 });
        setIsValidating(false);
        return;
      }

      // Parse existing state
      const parsedState: OnboardingState = JSON.parse(company.onboardingState);

      // Validate server exists
      if (parsedState.serverId) {
        try {
          const server = await serverService.getServerById(
            parsedState.serverId,
          );

          // Check if server is still being generated
          if (
            server.processingProgress !== undefined &&
            server.processingProgress < 100
          ) {
            // Server generation in progress, show progress step
            setCurrentStep(3);
            setOnboardingState({ ...parsedState, currentStep: 3 });
            setIsValidating(false);
            return;
          }

          // Validate widget exists
          if (parsedState.widgetId) {
            try {
              await widgetService.getWidgetById(parsedState.widgetId);

              // Check if any tools are enabled
              const tools = await toolService.getServerTools(
                parsedState.serverId,
              );
              const enabledTools = tools.filter((t) => t.isActive);

              if (enabledTools.length > 0) {
                // All setup complete, but onboarding not marked complete - go to success
                setCurrentStep(6);
                setOnboardingState({
                  ...parsedState,
                  currentStep: 6,
                  selectedToolIds: enabledTools.map((t) => t.id),
                });
              } else {
                // Server fully generated, tools not selected yet - check if actually complete
                if (server.processingProgress === 100 || server.isActive) {
                  // Server is fully generated, show tool selection
                  setCurrentStep(5);
                  setOnboardingState({ ...parsedState, currentStep: 5 });
                } else {
                  // Server generation might have failed or still in progress
                  setCurrentStep(3);
                  setOnboardingState({ ...parsedState, currentStep: 3 });
                }
              }
            } catch {
              // Widget missing, restart from auth config
              setCurrentStep(2);
              setOnboardingState({
                ...parsedState,
                currentStep: 2,
                widgetId: undefined,
              });
            }
          } else {
            // No widget, restart from auth config
            setCurrentStep(2);
            setOnboardingState({ ...parsedState, currentStep: 2 });
          }
        } catch {
          // Server missing, start from beginning
          setCurrentStep(0);
          setOnboardingState({ currentStep: 0 });
        }
      } else {
        // No server, use saved step or start from beginning
        setCurrentStep(parsedState.currentStep || 0);
        setOnboardingState(parsedState);
      }
    } catch (error) {
      console.error("Error validating onboarding state:", error);
      setCurrentStep(0);
      setOnboardingState({ currentStep: 0 });
    } finally {
      setIsValidating(false);
    }
  };

  const updateOnboardingState = async (updates: Partial<OnboardingState>) => {
    const newState = { ...onboardingState, ...updates };
    setOnboardingState(newState);

    try {
      await companyService.updateOnboardingState(newState);
    } catch (error) {
      console.error("Failed to update onboarding state:", error);
    }
  };

  const handleStep0Next = async () => {
    await updateOnboardingState({ currentStep: 1 });
    setCurrentStep(1);
  };

  const handleStep1Next = async (data: {
    docUrl: string;
    docType: ApiDocType;
    serverName: string;
    serverDescription: string;
    companyWebsite: string;
  }) => {
    try {
      // Persist Company.Website so the guardrails generator (which runs after Step 3) has it.
      // Best-effort — a failure here should not block onboarding.
      const currentCompany = companyService.getCurrentCompany();
      if (currentCompany?.id && data.companyWebsite) {
        try {
          await companyService.updateCompany(currentCompany.id, {
            website: data.companyWebsite,
          });
          await authService.fetchAndStoreCurrentUser();
        } catch (err) {
          console.warn(
            "Failed to persist company website during onboarding",
            err,
          );
        }
      }

      // Create server immediately and start generation in background
      const serverResponse = await serverService.createServer({
        name: data.serverName,
        description: data.serverDescription,
        specUrl: data.docUrl,
        apiDocType: data.docType,
        autoGeneratePrompts: false,
        isOnboarding: true,
      });

      serverGenerationHubService.notifyGenerationStarted(serverResponse.id);

      await updateOnboardingState({
        currentStep: 2,
        docUrl: data.docUrl,
        docType: data.docType.toString(),
        serverName: data.serverName,
        serverDescription: data.serverDescription,
        serverId: serverResponse.id,
        companyWebsite: data.companyWebsite,
      });
      setCurrentStep(2);
    } catch (error: any) {
      toaster.create({
        title: t("app.onboarding.errors.createServerFailed"),
        description: error.message || t("app.common.errors.genericError"),
        type: "error",
      });
    }
  };

  const handleStep2Next = async (data: {
    serverId: string;
    widgetId: string;
    authConfig: any;
  }) => {
    await updateOnboardingState({
      currentStep: 3,
      serverId: data.serverId,
      widgetId: data.widgetId,
      authConfig: data.authConfig,
    });
    setCurrentStep(3);
  };

  const handleStep3Complete = async (suggestedMaskingRulesCount: number) => {
    // Fire-and-forget: generate a domain-aware guardrails policy now that tools have been extracted.
    // The endpoint reads Company.Website + the company's active servers/tools. If website is missing
    // or generation fails, the slim system policy stays in effect — onboarding is unaffected.
    void guardrailsService.regenerate().catch((err) => {
      console.warn("Guardrails regeneration during onboarding failed", err);
    });

    if (suggestedMaskingRulesCount > 0) {
      await updateOnboardingState({ currentStep: 7 });
      setCurrentStep(7);
    } else {
      await updateOnboardingState({ currentStep: 5 });
      setCurrentStep(5);
    }
  };

  const handleStep7Next = async () => {
    await updateOnboardingState({ currentStep: 5 });
    setCurrentStep(5);
  };

  const handleStep3Error = () => {
    setCurrentStep(4);
  };

  const handleStep4Retry = () => {
    setCurrentStep(3);
  };

  const handleStep4StartOver = async () => {
    await updateOnboardingState({ currentStep: 1 });
    setCurrentStep(1);
  };

  const handleStep5Next = async (selectedToolIds: string[]) => {
    try {
      // Save final state
      await updateOnboardingState({
        currentStep: 6,
        selectedToolIds,
      });

      // Mark onboarding as completed
      await companyService.completeOnboarding();

      setCurrentStep(6);
    } catch (error: any) {
      toaster.create({
        title: t("app.onboarding.errors.completeFailed"),
        description: error.message || t("app.common.errors.genericError"),
        type: "error",
      });
    }
  };

  const handleStep6Complete = () => {
    // Close modal and stay in app
    onClose();
  };

  const getHelpVideoForStep = () => {
    const videoKeys: Record<number, string> = {
      1: "onboarding.step1.apiDocumentation",
      2: "onboarding.step2.authorization",
      3: "onboarding.step3.generationProgress",
      4: "onboarding.step4.errorHandling",
      5: "onboarding.step5.toolSelection",
      6: "onboarding.step6.success",
      7: "onboarding.step7.maskingRulesReview",
    };

    const videoKey = videoKeys[currentStep];
    return videoKey ? getHelpVideoUrl(videoKey) : null;
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 0:
        return {
          title: t("app.onboarding.step0.title"),
          description: t("app.onboarding.step0.description"),
        };
      case 1:
        return {
          title: t("app.onboarding.step1.title"),
          description: t("app.onboarding.step1.description"),
        };
      case 2:
        return {
          title: t("app.onboarding.step2.title"),
          description: t("app.onboarding.step2.description"),
        };
      case 3:
        return {
          title: t("app.onboarding.step3.title"),
          description: t("app.onboarding.step3.description"),
        };
      case 4:
        return {
          title: t("app.onboarding.step4.title"),
          description: t("app.onboarding.step4.description"),
        };
      case 5:
        return {
          title: t("app.onboarding.step5.title"),
          description: t("app.onboarding.step5.description"),
        };
      case 6:
        return {
          title: t("app.onboarding.step6.title"),
          description: t("app.onboarding.step6.description"),
        };
      case 7:
        return {
          title: t("app.onboarding.step7.title"),
          description: t("app.onboarding.step7.description"),
        };
      default:
        return { title: t("app.onboarding.title"), description: "" };
    }
  };

  const getCoreCheckpoints = () => {
    return [
      {
        label: t("app.onboarding.checkpoints.createServer"),
        completed: !!onboardingState.serverId && currentStep >= 3,
      },
      {
        label: t("app.onboarding.checkpoints.configureAuth"),
        completed: !!onboardingState.widgetId && currentStep >= 3,
      },
      {
        label: t("app.onboarding.checkpoints.selectTools"),
        completed:
          !!onboardingState.selectedToolIds &&
          onboardingState.selectedToolIds.length > 0,
      },
    ];
  };

  if (!isOpen) return null;

  if (isValidating) {
    return (
      <Box
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg={getThemeColor("modal.overlay")}
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex="1000002"
      >
        <Box
          bg={getThemeColor("modal.bg")}
          borderRadius="xl"
          boxShadow="2xl"
          p={10}
          textAlign="center"
        >
          <Spinner size="lg" />
        </Box>
      </Box>
    );
  }

  const checkpoints = getCoreCheckpoints();

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg={getThemeColor("modal.overlay")}
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex="1000002"
      p={{ base: 0, md: 4 }}
    >
      <Box
        bg={getThemeColor("modal.bg")}
        borderRadius={{ base: "0", md: "xl" }}
        boxShadow="2xl"
        w={{ base: "100%", md: "80%" }}
        h={{ base: "100%", md: "80%" }}
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <HStack align="stretch" gap={0} h="full">
          {/* Left Sidebar - Hidden on small screens */}
          <Box
            w="280px"
            bg={getThemeColor("modal.bg")}
            borderRightWidth="1px"
            borderColor={getThemeColor("border.default")}
            py={6}
            px={6}
            flexShrink={0}
            display={{ base: "none", lg: "flex" }}
            flexDirection="column"
          >
            {/* Step Info at Top */}
            <VStack align="stretch" gap={2} mb={6}>
              <Text
                fontSize="xs"
                color={getThemeColor("text.secondary")}
                fontWeight="medium"
              >
                {t("app.onboarding.stepProgress", {
                  current: Math.min(currentStep + 1, 8),
                  total: 8,
                })}
              </Text>
              <Heading
                size="md"
                color={getThemeColor("text.primary")}
                lineHeight="shorter"
              >
                {getStepTitle().title}
              </Heading>
              <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                {getStepTitle().description}
              </Text>
            </VStack>

            {/* Spacer to push content to bottom */}
            <Box flex="1" />

            {/* Help Video - Show for steps 1-6 */}
            {getHelpVideoForStep() && (
              <Box mb={4}>
                <Text
                  fontSize="xs"
                  color={getThemeColor("text.secondary")}
                  fontWeight="medium"
                  mb={2}
                >
                  {t("app.onboarding.helpVideo")}
                </Text>
                <Box
                  w="full"
                  borderRadius="md"
                  overflow="hidden"
                  boxShadow="md"
                  bg={getThemeColor("bg.tertiary")}
                  position="relative"
                  paddingBottom="56.25%"
                >
                  <iframe
                    src={`https://www.youtube.com/embed/${getHelpVideoForStep()}`}
                    style={{
                      border: "none",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                    allowFullScreen
                    title={t("app.onboarding.helpVideo")}
                  />
                </Box>
              </Box>
            )}

            {/* Core Checkpoints at Bottom */}
            <VStack align="stretch" gap={4}>
              <Box>
                <Text
                  fontSize="xs"
                  color={getThemeColor("text.secondary")}
                  fontWeight="medium"
                  mb={3}
                >
                  {t("app.onboarding.coreCheckpoints")}
                </Text>
              </Box>

              {checkpoints.map((checkpoint, index) => (
                <HStack key={index} gap={3} align="start">
                  <Box
                    mt={0.5}
                    fontSize="md"
                    color={
                      checkpoint.completed
                        ? "green.500"
                        : getThemeColor("text.tertiary")
                    }
                  >
                    {checkpoint.completed ? <FaCheck /> : <FaCircle />}
                  </Box>
                  <Text
                    fontSize="sm"
                    fontWeight={checkpoint.completed ? "semibold" : "normal"}
                    color={
                      checkpoint.completed
                        ? "green.700"
                        : getThemeColor("text.secondary")
                    }
                  >
                    {checkpoint.label}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>

          {/* Main Content Area */}
          <Box flex="1" display="flex" flexDirection="column" overflow="hidden">
            {/* Header - Visible only on small screens when sidebar is hidden */}
            <Box
              borderBottomWidth="1px"
              borderColor={getThemeColor("border.default")}
              bg={getThemeColor("modal.bg")}
              py={4}
              px={6}
              flexShrink={0}
              boxShadow="sm"
              display={{ base: "block", lg: "none" }}
            >
              <Box>
                <Text
                  fontSize="xs"
                  color={getThemeColor("text.secondary")}
                  fontWeight="medium"
                  mb={1}
                >
                  {t("app.onboarding.stepProgress", {
                    current: Math.min(currentStep + 1, 8),
                    total: 8,
                  })}
                </Text>
                <Heading size="lg" mb={1} color={getThemeColor("text.primary")}>
                  {getStepTitle().title}
                </Heading>
                <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                  {getStepTitle().description}
                </Text>
              </Box>
            </Box>

            {/* Step Content - Scrollable with steps managing their own footers */}
            <Box
              flex="1"
              overflowY="auto"
              position="relative"
              pt={{ base: 0, lg: 6 }}
            >
              {currentStep === 0 && <Step0Overview onNext={handleStep0Next} />}

              {currentStep === 1 && (
                <Step1ApiDocumentation
                  onNext={handleStep1Next}
                  onBack={() => setCurrentStep(0)}
                  initialData={{
                    docUrl: onboardingState.docUrl,
                    docType: onboardingState.docType
                      ? parseInt(onboardingState.docType)
                      : undefined,
                    serverName: onboardingState.serverName,
                    serverDescription: onboardingState.serverDescription,
                    companyWebsite:
                      onboardingState.companyWebsite ||
                      companyService.getCurrentCompany()?.website,
                  }}
                />
              )}

              {currentStep === 2 && (
                <Step2Authorization
                  onNext={handleStep2Next}
                  onBack={() => setCurrentStep(1)}
                  serverData={{
                    serverId: onboardingState.serverId || "",
                    serverName: onboardingState.serverName || "API Server",
                    serverDescription:
                      onboardingState.serverDescription || "API Server",
                    docUrl: onboardingState.docUrl || "",
                    docType: parseInt(onboardingState.docType || "0"),
                  }}
                  initialData={{
                    authConfig: onboardingState.authConfig,
                  }}
                />
              )}

              {currentStep === 3 && onboardingState.serverId && (
                <Step3GenerationProgress
                  serverId={onboardingState.serverId}
                  onComplete={handleStep3Complete}
                  onError={handleStep3Error}
                />
              )}

              {currentStep === 4 && (
                <Step4ErrorHandling
                  serverId={onboardingState.serverId || ""}
                  onRetry={handleStep4Retry}
                  onStartOver={handleStep4StartOver}
                />
              )}

              {currentStep === 5 && onboardingState.serverId && (
                <Step5ToolSelection
                  serverId={onboardingState.serverId}
                  onNext={handleStep5Next}
                  initialData={{
                    selectedToolIds: onboardingState.selectedToolIds,
                  }}
                />
              )}

              {currentStep === 6 && (
                <Step6Success
                  serverId={onboardingState.serverId || ""}
                  serverName={onboardingState.serverName || "API Server"}
                  widgetId={onboardingState.widgetId || ""}
                  toolCount={onboardingState.selectedToolIds?.length || 0}
                  onComplete={handleStep6Complete}
                />
              )}

              {currentStep === 7 && onboardingState.serverId && (
                <Step7MaskingRulesReview
                  serverId={onboardingState.serverId}
                  onNext={handleStep7Next}
                />
              )}
            </Box>
          </Box>
        </HStack>
      </Box>
    </Box>
  );
};
